// "Your servers" page — roster management. Add a new row (just a label —
// the engine dials in with the minted token), reveal/rotate tokens, kick a
// server, switch to one. Single-server users get auto-entered upstream in
// App.tsx and never see this page; multi-server users land here to pick.

import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { vpsApi } from "../api/vps";
import type { VpsRow, VpsRowWithToken } from "../api/vps";
import { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";

export function VpsListPage() {
  const { enterVps, activeVpsId } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();

  // Poll the roster every 3s so the connected pills go live without a
  // manual refresh. Cheap query — single SQL select + a DashMap lookup
  // per row.
  const listQ = useQuery({
    queryKey: ["vps", "list"],
    queryFn: vpsApi.list,
    refetchInterval: 3000,
    refetchIntervalInBackground: false,
  });

  const remove = useMutation({
    mutationFn: (id: number) => vpsApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["vps", "list"] });
    },
  });

  async function onSwitch(row: VpsRow) {
    await enterVps(row.id);
    nav("/desk");
  }

  return (
    <div className="app-main">
      <h2 style={{ marginTop: 0 }}>Your servers</h2>

      {!listQ.isLoading && (listQ.data?.length ?? 0) === 0 && (
        <div className="card info-banner" style={{ marginBottom: 16 }}>
          <h2 style={{ marginTop: 0 }}>First-time setup</h2>
          <p>
            You haven't added a server yet. The wizard walks you through
            it in three short steps — no Tailscale required.
          </p>
          <Link
            to="/onboarding"
            className="primary"
            style={{ display: "inline-block", padding: "8px 12px", textDecoration: "none" }}
          >
            Set up your server →
          </Link>
        </div>
      )}

      <AddVpsForm
        onCreated={() => qc.invalidateQueries({ queryKey: ["vps", "list"] })}
      />

      <div className="card">
        <h2>Roster</h2>
        {listQ.isLoading && <div className="muted">loading…</div>}
        {listQ.error && (
          <div className="error-banner">
            {listQ.error instanceof ApiError ? listQ.error.message : "failed to load"}
          </div>
        )}
        {!listQ.isLoading && (listQ.data?.length ?? 0) === 0 && (
          <div className="muted">No servers yet. Add one above or use the wizard.</div>
        )}
        {listQ.data && listQ.data.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Label</th>
                <th>Engine</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {listQ.data.map((r) => {
                const active = r.id === activeVpsId;
                return (
                  <tr key={r.id}>
                    <td>
                      <InlineLabelEditor row={r} />
                      {active && (
                        <span className="pill ghost" style={{ marginLeft: 8 }}>
                          active
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={r.engine_connected ? "pill ghost" : "pill dim"}>
                        {r.engine_connected ? "✓ connected" : "offline"}
                      </span>
                    </td>
                    <td className="muted small">
                      {r.created_at.replace("T", " ").slice(0, 16)}
                    </td>
                    <td className="right">
                      {!active && r.engine_connected && (
                        <button onClick={() => void onSwitch(r)}>Switch to</button>
                      )}{" "}
                      <TokenActions row={r} />{" "}
                      <button
                        className="danger"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (
                            confirm(
                              `Remove "${r.label}"? Any live engine WS will be kicked. The token is invalidated permanently.`,
                            )
                          ) {
                            remove.mutate(r.id);
                          }
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/** Inline label editor: shows label text + edit pencil, inline input on click. */
function InlineLabelEditor({ row }: { row: VpsRow }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row.label);
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);

  const renameM = useMutation({
    mutationFn: (label: string) => vpsApi.rename(row.id, label),
    onSuccess: () => {
      setEditing(false);
      setErr(null);
      void qc.invalidateQueries({ queryKey: ["vps", "list"] });
    },
    onError: (e) => {
      setErr(e instanceof ApiError ? e.message : "rename failed");
    },
  });

  // Sync draft when row changes (e.g. after successful rename)
  useEffect(() => {
    setDraft(row.label);
  }, [row.label]);

  if (editing) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            const trimmed = draft.trim();
            if (trimmed && trimmed !== row.label) {
              renameM.mutate(trimmed);
            } else {
              setDraft(row.label);
              setEditing(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              (e.target as HTMLInputElement).blur();
            }
            if (e.key === "Escape") {
              setDraft(row.label);
              setEditing(false);
            }
          }}
          style={{ width: 120, fontSize: "inherit" }}
          autoFocus
        />
        {renameM.isPending && <span className="muted small">…</span>}
        {err && <span className="muted small" style={{ color: "var(--danger)" }}>{err}</span>}
      </span>
    );
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span>{row.label}</span>
      <button
        type="button"
        className="btn-xs"
        style={{ padding: "0 3px", fontSize: 11, lineHeight: "1.2" }}
        onClick={() => setEditing(true)}
        title="Rename"
        aria-label={`Rename ${row.label}`}
      >
        ✎
      </button>
    </span>
  );
}

// Per-row token controls. The plaintext token is stored as a hash on the
// server and cannot be retrieved — the only way to recover from a lost
// token is to rotate. The new token is revealed once after rotation and
// disappears from the UI after 30 s; a live engine is kicked immediately
// and must reconnect with the new token.
function TokenActions({ row }: { row: VpsRow }) {
  const [rotated, setRotated] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const rotateM = useMutation({
    mutationFn: () => vpsApi.rotateToken(row.id),
    onSuccess: (resp) => {
      setRotated(resp.connection_token);
      setErr(null);
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "rotate failed"),
  });

  useEffect(() => {
    if (!rotated) return;
    const t = setTimeout(() => setRotated(null), 30_000);
    return () => clearTimeout(t);
  }, [rotated]);

  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      <button
        type="button"
        className="danger"
        disabled={rotateM.isPending}
        onClick={() => {
          if (
            confirm(
              `Rotate the token for "${row.label}"? The live engine will be disconnected immediately and must reconnect with the new token (re-run start-engine.ps1 with the value shown).`,
            )
          ) {
            rotateM.mutate();
          }
        }}
      >
        {rotateM.isPending ? "…" : "Rotate token"}
      </button>
      {rotated && (
        <TokenReveal token={rotated} rotated onDismiss={() => setRotated(null)} />
      )}
      {err && <span className="muted small" style={{ color: "var(--danger)" }}>{err}</span>}
    </span>
  );
}

function TokenReveal({
  token,
  rotated,
  onDismiss,
}: {
  token: string;
  rotated: boolean;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        marginLeft: 6,
        padding: "2px 6px",
        background: "var(--code-bg, #1a1a1a)",
        borderRadius: 4,
      }}
    >
      <code className="mono small">{token}</code>
      <button type="button" onClick={() => void copy()} style={{ fontSize: 11 }}>
        {copied ? "Copied ✓" : "Copy"}
      </button>
      <button type="button" onClick={onDismiss} style={{ fontSize: 11 }}>
        Hide
      </button>
      {rotated && (
        <span className="muted small" style={{ marginLeft: 4 }}>
          new — old token revoked
        </span>
      )}
    </span>
  );
}

function AddVpsForm({ onCreated }: { onCreated: () => void }) {
  const [label, setLabel] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [justCreated, setJustCreated] = useState<VpsRowWithToken | null>(null);

  const create = useMutation({
    mutationFn: () => vpsApi.create({ label: label.trim() }),
    onSuccess: (row) => {
      setJustCreated(row);
      setLabel("");
      setErr(null);
      onCreated();
    },
    onError: (e) => {
      setErr(e instanceof ApiError ? e.message : "create failed");
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    create.mutate();
  }

  return (
    <form
      className="card"
      onSubmit={submit}
    >
      <h2>Add a server</h2>
      {err && <div className="error-banner">{err}</div>}
      {justCreated && (
        <div className="info-banner">
          <strong>Token minted for "{justCreated.label}"</strong> — copy it now,
          we won't show it again on this page.
          <div style={{ marginTop: 6 }}>
            <TokenReveal
              token={justCreated.connection_token}
              rotated={false}
              onDismiss={() => setJustCreated(null)}
            />
          </div>
          <div className="muted small" style={{ marginTop: 6 }}>
            Run <code>start-engine.ps1 -UiHost &lt;wss-url&gt; -Token &lt;token&gt;</code>
            on your server. Or use the{" "}
            <Link to="/onboarding">guided wizard</Link> if it's a fresh box.
          </div>
        </div>
      )}
      <div className="grid grid-2">
        <label>
          <span className="label-text">Label</span>
          <input
            value={label}
            placeholder="prod"
            onChange={(e) => setLabel(e.target.value)}
          />
          <span className="label-help">Anything you want — shows up in your roster.</span>
        </label>
      </div>
      <button
        className="primary"
        type="submit"
        style={{ marginTop: 12 }}
        disabled={create.isPending || !label.trim()}
      >
        {create.isPending ? "Minting…" : "Add server"}
      </button>
    </form>
  );
}